import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Clock, ExternalLink, FileText, Heart, MapPin, Star, UtensilsCrossed } from "lucide-react";

import { SmartImage } from "@/components/ui/SmartImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLiveStatus } from "@/components/districts/OpenStatusPill";
import { useFavorites } from "@/hooks/useFavorites";
import { useReviewStat } from "@/hooks/use-review-stats";
import { useGeolocation, distanceKm, formatDistance } from "@/hooks/useGeolocation";
import type { DistrictVenueDTO } from "@/lib/districts.functions";
import {
  DAY_KEYS,
  DAY_LABELS,
  computeOpenStatus,
  dayOf,
  formatDayHours,
  helsinkiNow,
  servesToday,
} from "@/lib/opening-hours";

type FilterKey = "all" | "open" | "lunch" | "dinner" | "drinks";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "open", label: "Open now" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
  { key: "drinks", label: "Drinks" },
  { key: "all", label: "All venues" },
];

function hasMenu(v: DistrictVenueDTO) {
  return !!(v.menu_pdf_url || v.menu_image_url || v.menu_external_url);
}

function isDrinks(v: DistrictVenueDTO) {
  const hay = `${v.category ?? ""} ${(v.tags ?? []).join(" ")}`.toLowerCase();
  return /bar|pub|cocktail|brewery|panimo|wine|drink/.test(hay);
}

function servesDinner(v: DistrictVenueDTO) {
  const { dayKey } = helsinkiNow();
  const day = dayOf(v.opening_hours, dayKey);
  if (!day || day.closed || !day.close) return false;
  const close = Number(day.close.slice(0, 2));
  // Open past 18:00 counts as a dinner venue.
  return close === 0 || close >= 18 || close < 6;
}

export function DistrictVenueList({ venues }: { venues: DistrictVenueDTO[] }) {
  const [filter, setFilter] = useState<FilterKey>("all");
  const [menuVenue, setMenuVenue] = useState<DistrictVenueDTO | null>(null);

  const filtered = useMemo(() => {
    return venues.filter((v) => {
      if (filter === "all") return true;
      if (filter === "open") {
        const s = computeOpenStatus(v);
        return s.state === "open" || s.state === "closing_soon";
      }
      if (filter === "lunch") return servesToday(v.lunch_hours) || /lunch|lounas/i.test(v.category ?? "");
      if (filter === "dinner") return servesDinner(v);
      if (filter === "drinks") return isDrinks(v);
      return true;
    });
  }, [venues, filter]);

  const openCount = useMemo(
    () => venues.filter((v) => ["open", "closing_soon"].includes(computeOpenStatus(v).state)).length,
    [venues],
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={active}
              className={`rounded-full border px-3.5 py-2 text-[12px] font-semibold tracking-tight transition-colors ${
                active
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy/70 border-navy/15 hover:border-navy/40"
              }`}
            >
              {f.label}
              {f.key === "open" ? ` · ${openCount}` : ""}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-navy/15 bg-white/60 p-8 text-center text-sm text-navy/60">
          No venues match this filter right now. Try “All venues”.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {filtered.map((v) => (
            <VenueCard key={v.id} venue={v} onOpenMenu={() => setMenuVenue(v)} />
          ))}
        </div>
      )}

      <MenuDialog venue={menuVenue} onClose={() => setMenuVenue(null)} />
    </div>
  );
}

function VenueCard({ venue, onOpenMenu }: { venue: DistrictVenueDTO; onOpenMenu: () => void }) {
  const status = useLiveStatus(venue);
  const { has, toggle } = useFavorites();
  const stat = useReviewStat(venue.slug);
  const { state: geo } = useGeolocation(true);
  const saved = has(venue.id);
  const dist =
    geo.status === "granted" && venue.lat != null && venue.lng != null
      ? distanceKm(geo, { lat: venue.lat, lng: venue.lng })
      : null;
  const area = venue.area || "Pasila";
  const tone =
    status.state === "open"
      ? "text-emerald-600"
      : status.state === "closing_soon"
        ? "text-amber-600"
        : status.state === "closed"
          ? "text-rose-600"
          : "text-slate-500";
  const dot =
    status.state === "open"
      ? "bg-emerald-500"
      : status.state === "closing_soon"
        ? "bg-amber-500"
        : status.state === "closed"
          ? "bg-rose-500"
          : "bg-slate-400";
  const badge =
    status.state === "open"
      ? "OPEN NOW"
      : status.state === "closing_soon"
        ? "CLOSING SOON"
        : status.state === "closed"
          ? "CLOSED"
          : "HOURS N/A";

  return (
    <article className="group overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-shadow hover:shadow-lg">
      <div className="relative">
        <Link to="/place/$id" params={{ id: venue.slug }} className="block">
          <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
            {venue.image ? (
              <SmartImage
                src={venue.image}
                alt={venue.name}
                className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-navy/20">
                <UtensilsCrossed className="size-8" />
              </div>
            )}
            <div className="absolute left-3 top-3 overflow-hidden rounded-lg bg-white/95 shadow-sm backdrop-blur">
              <div
                style={{ textTransform: "none" }}
                className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}
              >
                <span className={`size-1.5 rounded-full ${dot} ${status.state === "open" ? "animate-pulse" : ""}`} />
                {badge}
              </div>
              <div style={{ textTransform: "none" }} className="px-2 pb-1 text-[11px] font-semibold text-navy/70">
                {status.todayLabel}
              </div>
            </div>
          </div>
        </Link>
        <button
          type="button"
          aria-label={saved ? "Remove from favourites" : "Save to favourites"}
          onClick={() => toggle(venue.id)}
          className="absolute right-3 top-3 grid size-8 place-items-center rounded-full bg-white/95 shadow-sm backdrop-blur transition-transform hover:scale-105"
        >
          <Heart className={`size-4 ${saved ? "fill-rose-500 text-rose-500" : "text-navy/60"}`} />
        </button>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <Link to="/place/$id" params={{ id: venue.slug }} className="min-w-0">
            <h3 className="font-heading text-lg leading-tight text-navy">{venue.name}</h3>
            {dist != null ? (
              <span className="mt-1 inline-flex w-fit items-center rounded-full bg-navy/[0.06] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-navy/70">
                {formatDistance(dist)}
              </span>
            ) : null}
            <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-navy/70">
              <MapPin className="size-3 shrink-0 text-teal" strokeWidth={2.25} />
              <span className="truncate">{area}</span>
            </p>
          </Link>
          {stat.count > 0 ? (
            <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-navy">
              {stat.avg.toFixed(1)}
              <Star className="size-3.5 fill-navy text-navy" />
            </span>
          ) : null}
        </div>
        <p style={{ textTransform: "none" }} className="mt-1 line-clamp-1 text-xs text-navy/50">
          {[venue.category, ...(venue.tags ?? []).slice(0, 2)].filter(Boolean).join(" · ")}
        </p>

        <p style={{ textTransform: "none" }} className={`mt-2 text-xs font-semibold ${tone}`}>
          {status.label}
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-navy/70">
          <Clock className="size-3.5 shrink-0 text-navy/40" />
          <span style={{ textTransform: "none" }}>Today {status.todayLabel}</span>
        </p>

        {venue.address ? (
          <p className="mt-1 flex items-center gap-1.5 text-xs text-navy/50">
            <MapPin className="size-3.5 shrink-0" />
            <span className="truncate">{venue.address}</span>
          </p>
        ) : null}

        <div className="mt-4 flex items-center gap-2">
          {venue.has_dish_menu ? (
            <Link
              to="/menu/$slug"
              params={{ slug: venue.slug }}
              className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-royal"
            >
              <FileText className="size-3.5" />
              View menu
            </Link>
          ) : hasMenu(venue) ? (
            <button
              type="button"
              onClick={onOpenMenu}
              className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-royal"
            >
              <FileText className="size-3.5" />
              View menu
            </button>
          ) : null}

          <Link
            to="/place/$id"
            params={{ id: venue.slug }}
            className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy/70 transition-colors hover:border-navy/40"
          >
            Details
          </Link>
        </div>
      </div>
    </article>
  );
}

function MenuDialog({ venue, onClose }: { venue: DistrictVenueDTO | null; onClose: () => void }) {
  return (
    <Dialog open={!!venue} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{venue?.name} — menu</DialogTitle>
        </DialogHeader>
        {venue ? (
          <div className="space-y-4">
            {venue.menu_updated_at ? (
              <p style={{ textTransform: "none" }} className="text-xs text-navy/50">
                Updated {new Date(venue.menu_updated_at).toLocaleDateString()}
              </p>
            ) : null}

            {venue.menu_image_url ? (
              <SmartImage src={venue.menu_image_url} alt={`${venue.name} menu`} className="w-full rounded-xl border" />
            ) : null}

            <div className="flex flex-wrap gap-2">
              {venue.menu_pdf_url ? (
                <a
                  href={venue.menu_pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white"
                >
                  <FileText className="size-3.5" /> Open menu PDF
                </a>
              ) : null}
              {venue.menu_external_url ? (
                <a
                  href={venue.menu_external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy/70"
                >
                  <ExternalLink className="size-3.5" /> Menu on their site
                </a>
              ) : null}
            </div>

            <div className="rounded-xl border border-navy/10 p-4">
              <h4 className="font-heading text-sm text-navy">Opening hours</h4>
              <ul className="mt-2 space-y-1 text-sm text-navy/70">
                {DAY_KEYS.map((d) => (
                  <li key={d} className="flex justify-between gap-6">
                    <span>{DAY_LABELS[d]}</span>
                    <span style={{ textTransform: "none" }}>{formatDayHours(dayOf(venue.opening_hours, d))}</span>
                  </li>
                ))}
              </ul>
              {venue.kitchen_hours ? (
                <p style={{ textTransform: "none" }} className="mt-3 text-xs text-navy/50">
                  Kitchen today: {formatDayHours(dayOf(venue.kitchen_hours, helsinkiNow().dayKey))}
                </p>
              ) : null}
              {venue.lunch_hours ? (
                <p style={{ textTransform: "none" }} className="text-xs text-navy/50">
                  Lunch today: {formatDayHours(dayOf(venue.lunch_hours, helsinkiNow().dayKey))}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default DistrictVenueList;
